import { useEffect, useState, Fragment } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Alert,
  Box,
  Typography,
  Container,
  Stack,
  Fab,
  Badge,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { ReactComponent as PolicyIcon } from "../assets/icons/Shield-Plus-Light--Streamline-Phosphor.svg";
import { ReactComponent as CatalogueIcon } from "../assets/icons/Common-File-Give-Hand-3--Streamline-Ultimate.svg";
import ConnectorAssetElastic from "./ConnectorAssetElastic";
import ConnectorAssetPolicy from "./ConnectorAssetPolicy";
import ConnectorFacets from "./ConnectorFacets";
import SearchBar from "./SearchBar";
import useSearch from "./useSearch";
import { SEARCH_URL, URL_REGISTRY } from "../config/settings";
import { useTranslator } from "../context/TranslatorContext";
import { useTheme } from "@mui/material/styles";

export default function HomePage() {
  const theme = useTheme();
  const location = useLocation();
  const { messages, language } = useTranslator();
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [openPolicy, setOpenPolicy] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const { searchValue, setSearchValue, data, totalItems, isLoading, handleSearch, clearAllSearchFilters,
    handleFacetsSave, handleFacetsClose, handleFacetsOpen, page, setPage,
    pageSize, setPageSize, sortOption, setSortOption, selectedFacets, setSelectedFacets,
    openFacets, facets, handleSearchWithParams } = useSearch("", SEARCH_URL, "name-asc", [], language);

  useEffect(() => {
    // console.log("Restoration effect triggered, location.key:", location.key);
    const paramsObj = Object.fromEntries([...searchParams]);
    // Restore all values from URL
    const restoredSearch = paramsObj.search || "";
    const restoredPage = parseInt(paramsObj.page || "1", 10);
    const restoredSize = parseInt(paramsObj.pageSize || "20", 10);
    const restoredSort = paramsObj.sort || "name-asc";
    // Restore facets
    const restoredFacets = [];
    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith("terms__")) {
        restoredFacets.push({
          name: key.replace("terms__", ""),
          key: value,
        });
      }
    }
    setSearchValue(restoredSearch);
    setPage(restoredPage);
    setPageSize(restoredSize);
    setSortOption(restoredSort);
    setSelectedFacets(restoredFacets);

    handleSearchWithParams(restoredSearch, restoredFacets, restoredPage, restoredSize, restoredSort);
  }, [location.key, language]); // Use location.key to detect Back navigation

  const updateURL = (facetsToUse = selectedFacets, overrides = {}) => {
    const params = new URLSearchParams();
    // facets
    (facetsToUse || []).forEach((f) => {
      params.append(`terms__${f.name}`, f.key);
    });
    // search
    if (overrides.searchValue !== undefined) {
      if (overrides.searchValue) params.append("search", overrides.searchValue);
    } else if (searchValue) {
      params.append("search", searchValue);
    }
    // pagination & sort
    params.append("page", String(overrides.page ?? page));
    params.append("pageSize", String(overrides.pageSize ?? pageSize));
    params.append("sort", overrides.sortOption ?? sortOption);
    setSearchParams(params);
  };

  const handleClearAllFilters = () => {
    clearAllSearchFilters(); // resets searchValue + facets in hook.[file:2]
    setPage(1);
    setPageSize(20);
    setSortOption("name-asc");
    updateURL([], { searchValue: "", page: 1, pageSize: 20, sortOption: "name-asc" });
    // handleSearch([]);
  };

  const handleRemoveFacet = (facet) => {
    const updatedFacets = selectedFacets.filter((f) => !(f.name === facet.name && f.key === facet.key));
    setSelectedFacets(updatedFacets);
    setPage(1);
    updateURL(updatedFacets, { page: 1 });
    handleSearchWithParams(searchValue, updatedFacets, 1, pageSize, sortOption);
  };

  const handleSortChange = (event) => {
    const value = event.target.value;
    setSortOption(value);
    setPage(1);
    updateURL(selectedFacets, { sortOption: value, page: 1 });
    // handleSearch(selectedFacets);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    updateURL(selectedFacets, { page: value });
    // handleSearch(selectedFacets);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value === "") {
      setPage(1);
      updateURL(selectedFacets, { searchValue: "", page: 1 });
    }
  };

  const handleSearchSubmit = () => {
    setPage(1);
    updateURL(selectedFacets, { searchValue: searchValue, page: 1 });
  };

  const handleFacetsSaveAndSync = (newFacets) => {
    handleFacetsSave(newFacets);
    updateURL(newFacets, { page: 1 });
    handleSearchWithParams(searchValue, newFacets, 1, pageSize, sortOption);
  };

  const facetNameMap = {
    lrType: messages.facets_filter_lrType,
    mediaType: messages.facets_filter_mediaType,
    modelFunction: messages.facets_filter_modelFunction,
    open: messages.facets_filter_open,
    public: messages.facets_filter_public,
    external: messages.facets_filter_external,
    isUsagePolicy: messages.facets_filter_isUsagePolicy,
    dctLanguage: messages.facets_filter_language,
    keyword: messages.facets_filter_keyword,
    type: messages.facets_filter_type
  };
  // console.log("home page facets" , selectedFacets);
  function searchBar() {
    return (
      <Fragment>
        <Grid container spacing={2} direction="row" justify="flex-start" alignItems="center" sx={{ mt: 2, mb: 2 }}>
          <Grid item xs={6} sm={8}>
            <SearchBar value={searchValue}
              onChange={handleSearchInputChange}
              onSearch={handleSearchSubmit}
              placeholder={messages.catalogue_search_assets_box} />
          </Grid>
          <Grid item xs={3} sm={2}>
            <Badge badgeContent={selectedFacets.length} color="primary" sx={{ width: "100%" }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleFacetsOpen}
                sx={{
                  "borderRadius": "20px",
                  "p": 2.05,
                  "width": "100%",
                  "borderColor": theme.palette.secondary.light, // Set border color if using 'outlined' variant
                  "&:hover": {
                    // backgroundColor: theme.palette.action.hover,
                    borderColor: theme.palette.primary.dark, // Adjust the border color on hover
                  },
                }}
                startIcon={<TuneIcon sx={{ fontSize: "2.125em", color: theme.palette.primary.dark }} />}
              >
                <Typography variant="h4" sx={{ color: theme.palette.primary.dark }}>
                  {messages.facets_main_button}
                </Typography>
              </Button>
            </Badge>

            <Dialog onClose={handleFacetsClose} open={openFacets} fullWidth maxWidth={"lg"}>
              <DialogTitle sx={{ fontSize: "1.725em" }}>{messages.facets_select_button}</DialogTitle>
              <ConnectorFacets
                facets={facets}
                selectedFacets={selectedFacets}
                onFacetChange={(newFacets) => {
                  setSelectedFacets(newFacets);
                  setPage(1);
                  updateURL(newFacets, { page: 1 });
                  handleSearchWithParams(searchValue, newFacets, 1, pageSize, sortOption);
                }}
                onSave={handleFacetsSaveAndSync} // This function is fine
                onClear={handleClearAllFilters}
                onClose={handleFacetsClose}
              />
            </Dialog>
          </Grid>
          <Grid item xs={3} sm={2}>
            <FormControl sx={{ m: 1, minWidth: 80, borderColor: theme.palette.secondary.light }}>
              <InputLabel id="sort-option-label">{messages.sort_option_title}</InputLabel>
              <Select
                labelId="sort-option-label"
                id="sort-option"
                label={messages.sort_option_title}
                value={sortOption}
                onChange={handleSortChange}
                sx={{
                  "borderColor": theme.palette.secondary.light, // Set border color if using 'outlined' variant
                  "&:hover": {
                    // backgroundColor: theme.palette.action.hover,
                    borderColor: theme.palette.primary.dark, // Adjust the border color on hover
                  },
                }}
              >
                <MenuItem value="name-asc">{messages.name_asc}</MenuItem>
                <MenuItem value="name-desc">{messages.name_desc}</MenuItem>
                <MenuItem value="createdAt-asc">{messages.date_asc}</MenuItem>
                <MenuItem value="createdAt-desc">{messages.date_desc}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ mt: 2, mb: 2, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            {selectedFacets.map((facet) => (
              <Chip
                key={facet.key + facet.name}
                label={`${facetNameMap[facet.name] || facet.name}: ${facet.key}`}
                onDelete={() => handleRemoveFacet(facet)}
              />
            ))}
            {totalItems > 0 && (
              <Typography variant="body2" sx={{ ml: 1, color: "text.secondary" }}>
                {`${totalItems} ${messages.search_results_label || messages.catalogue_search_total_items}`}
              </Typography>
            )}
          </Box>
        </Grid>
      </Fragment>
    );
  }

  function grid() {
    const datasetArray = data;
    return (
      <Grid container spacing={4}>
        {datasetArray.map((item, index) => {
          const policyButtonCMP = () => {
            return (
              <Grid item>
                <Fab
                  onClick={(e) => {
                    setOpenPolicy(!openPolicy);
                    setSelectedIndex(index);
                  }}
                  size="small"
                  variant="extended"
                  color="primary"
                >
                  <PolicyIcon
                    className="nav-icon"
                    style={{ marginRight: "0.5em" }}
                  />
                  <Typography variant="body2" sx={{ color: "#fff", p: 1 }}>
                    {messages.catalogue_view_policy_button}
                  </Typography>
                </Fab>
              </Grid>
            );
          };
          return (
            <Grid key={index} item xs={12} sm={6} md={6} sx={{ p: 1 }}>
              <Card elevation={0}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                    // borderColor: "primary.main",
                  },
                }}>

                <ConnectorAssetElastic
                  data={item}
                  index={index}
                  policyButtonCmp={policyButtonCMP}
                  language={language}
                />
                {openPolicy && index === selectedIndex && (
                  <ConnectorAssetPolicy
                    data={item}
                    validPolicy={item._source.policy}
                    contractPolicyTitle={item._source.contractPolicyTitle}
                    accessPolicyTitle={item._source.accessPolicyTitle}
                    contractPolicyLegalcode={
                      item._source.contractPolicyLegalcode
                    }
                    contractPolicyDescription={
                      item._source.contractPolicyDescription
                    }
                    index={selectedIndex}
                    handleClose={(e) => {
                      setOpenPolicy(false);
                    }}
                    open={openPolicy}
                  />
                )}
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Container sx={{ mb: 5, mt: 25 }}>
        <Stack direction="column" alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body1">Loading...</Typography>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return <p>Error: {error.message}</p>;
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 8 }}>
        <Box component="section" sx={{ pt: 2 }}>
          <div style={{ margin: "1em" }}>
            <Alert severity="info">{messages.updateBannerText}</Alert>
          </div>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "#D6E6F7",
              background: "#F4F8FD",
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 76,
                    height: 76,
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#eef2f6",
                    color: "#0F2651",
                  }}
                >
                  <CatalogueIcon style={{ width: 52, height: 52, fill: "warning.700" }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#0F2651",
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    {messages.joinTitle}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "rgba(0, 0, 46, 0.82)",
                      mb: 2,
                      lineHeight: 1.6,
                    }}
                  >
                    {messages.offersHelpText}
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => window.open(URL_REGISTRY, "_blank")}
                    sx={{
                      minWidth: { xs: "100%", sm: 200 },
                      alignSelf: "flex-start",
                      px: 3,
                      borderRadius: 2,
                      boxShadow: "none",
                    }}
                  >
                    {messages.joinButton}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>


        <Box component="section" sx={{ pt: 2 }}>
          {searchBar()}
        </Box>

        <Box component="section" sx={{ pb: 2 }}>
          {grid()}
        </Box>

        <Box
          component="section"
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={2}
        >
          <Pagination
            count={Math.ceil(totalItems / pageSize)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
          />
        </Box>
      </Container>
    </>
  );
}
